import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload, Plus, Search } from "lucide-react";
import apiClient from "@/api/apiClient";

import {
  useBloodTests,
  useCreateBloodTest,
  useUpdateBloodTest,
  useDeleteBloodTest,
} from "@/hooks/useBloodTests";
import { useAuth } from "@/hooks/useAuth";
import { showSuccess, showError, showWarning } from "@/utils/toastHelpers";

import UploadZone from "../components/blood-tests/UploadZone";
import BloodTestForm from "../components/blood-tests/BloodTestForm";
import PatientTestsView from "../components/blood-tests/PatientTestsView";
import DoctorTestsView from "../components/blood-tests/DoctorTestsView";

export default function BloodTests() {
  const [showForm, setShowForm] = useState(false);
  const [editingTest, setEditingTest] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const { user } = useAuth();

  // Query for fetching blood tests
  const {
    data: tests = [],
    isLoading,
    error,
  } = useBloodTests({
    orderBy: "testDate",
    orderDirection: "desc",
  });

  // Mutations
  const createMutation = useCreateBloodTest();
  const updateMutation = useUpdateBloodTest();
  const deleteMutation = useDeleteBloodTest();

  const handleFileUpload = async (file) => {
    try {
      // Upload the PDF to the server for extraction
      const response = await apiClient.upload("/api/blood-tests/upload", file);

      // The server returns both the created blood test and extraction summary
      if (response.bloodTest) {
        // Show success message with extraction summary
        const summary = response.extractionSummary;
        const message =
          `Successfully extracted blood test for ${summary.patientName}. ` +
          `Found ${summary.totalTests} test results` +
          (summary.abnormalResults.length > 0
            ? ` with ${summary.abnormalResults.length} abnormal values.`
            : ".");

        // You could show a toast or alert here
        console.log(message);
        // Close the upload modal
        setShowUpload(false);

        // The blood test list will refresh automatically via React Query
      }
    } catch (error) {
      console.error("Error processing file:", error);

      // Show user-friendly error message based on error type
      if (error.data?.code === "INVALID_BLOOD_TEST_DOCUMENT") {
        showError(
          "Invalid Document",
          `${error.data.details}\n\nPlease upload a valid blood test or pathology report PDF.`
        );
      } else if (error.data?.code === "MISSING_PATIENT_INFO") {
        showWarning(
          "Patient Information Missing",
          `${error.data.details}\n\nTip: You can add the blood test manually if the PDF doesn't contain clear patient information.`
        );
      } else if (error.data?.code === "EXTRACTION_SERVICE_ERROR") {
        showError("Service Unavailable", error.data.details);
      } else {
        showError("Upload Failed", error.data?.details || error.message);
      }
    }
  };

  const handleManualSubmit = async (testData) => {
    try {
      if (editingTest) {
        // Update existing test
        await updateMutation.mutateAsync({ testId: editingTest.id, testData });
        showSuccess("Blood test updated successfully");
      } else {
        // Create new test
        await createMutation.mutateAsync(testData);
        showSuccess("Blood test created successfully");
      }
      // Close form on success
      setShowForm(false);
      setEditingTest(null);
    } catch (error) {
      console.error("Error saving test:", error);
      // Show user-friendly error message
      if (error.status === 400 && error.data?.code === "MISSING_PATIENT_INFO") {
        showError("Missing patient information", error.data.details);
      } else {
        showError(
          "Failed to save blood test",
          error.data?.error || error.message
        );
      }
    }
  };

  const handleEditTest = (test) => {
    setEditingTest(test);
    setShowForm(true);
  };

  const handleDeleteTest = async (testId) => {
    if (
      window.confirm(
        "Are you sure you want to delete this blood test result? This action cannot be undone."
      )
    ) {
      try {
        await deleteMutation.mutateAsync(testId);
      } catch (error) {
        console.error("Error deleting test:", error);
      }
    }
  };

  const filteredTests = tests.filter((test) => {
    const matchesSearch =
      searchTerm === "" ||
      test.patientIdentificationNumber
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      test.patientEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      test.patientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      test.doctorName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      test.labName?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  // Determine if user is a patient (affects UI permissions)
  const isPatient = user && user.userType === "patient";

  // Show loading state
  if (isLoading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-slate-600">Loading blood tests...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-red-600 mb-4">Error loading blood tests</p>
            <p className="text-slate-600 text-sm">{error.message}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            {isPatient ? "My Blood Tests" : "Blood Tests"}
          </h1>
          <p className="text-slate-600">
            {isPatient
              ? "View your blood test results and health data"
              : "Upload and manage patient blood test results"}
          </p>
        </div>
        {!isPatient && (
          <div className="flex gap-3">
            <Button
              onClick={() => setShowUpload(true)}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg"
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload PDF
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setEditingTest(null); // Ensure no test is being edited when adding new
                setShowForm(true);
              }}
              className="border-slate-300 hover:bg-slate-50"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Manually
            </Button>
          </div>
        )}
      </div>

      {/* Search */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
          <Input
            placeholder={
              isPatient
                ? "Search by doctor or lab..."
                : "Search by patient ID, email, name, doctor, or lab..."
            }
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-white/60 backdrop-blur-sm border-slate-200/60"
          />
        </div>
      </div>

      {/* Upload Modal - Only for non-patients */}
      {!isPatient && showUpload && (
        <UploadZone
          onFileUpload={handleFileUpload}
          onClose={() => setShowUpload(false)}
        />
      )}

      {/* Manual Form Modal - Only for non-patients */}
      {!isPatient && showForm && (
        <BloodTestForm
          test={editingTest} // Pass the test data if editing
          onSubmit={handleManualSubmit}
          onClose={() => {
            setShowForm(false);
            setEditingTest(null); // Clear editing test when closing form
          }}
        />
      )}

      {/* Tests List */}
      {isPatient ? (
        <PatientTestsView tests={filteredTests} isLoading={isLoading} />
      ) : (
        <DoctorTestsView
          tests={filteredTests}
          isLoading={isLoading}
          onEdit={handleEditTest}
          onDelete={handleDeleteTest}
        />
      )}
    </div>
  );
}
